import { motion } from 'framer-motion';
import { useState } from 'react';
import { Send, Check, Loader2, Star, Quote } from 'lucide-react';
import emailjs from '@emailjs/browser';

const EMAILJS_SERVICE_ID = 'service_1ym2svp';
const EMAILJS_TEMPLATE_ID = 'template_e9vnfo2';
const EMAILJS_PUBLIC_KEY = 'C3As22TUfjzojO5km';

interface Testimonial {
  id: string;
  name: string;
  role: string;
  content: string;
  rating: number;
  date: string;
}

// Approved testimonials sorted by date (newest first)
const approvedTestimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Sarah Mitchell',
    role: 'Marketing Director, TechVision',
    content: 'Working with CAYLO was an incredible experience. The attention to detail and creative vision transformed our brand story into something truly memorable.',
    rating: 5,
    date: '2024-01-15',
  },
  {
    id: '2',
    name: 'James Chen',
    role: 'Independent Artist',
    content: 'The music video exceeded all my expectations. CAYLO understood my vision perfectly and added creative elements I never imagined.',
    rating: 5,
    date: '2024-01-08',
  },
  {
    id: '3',
    name: 'Emma Rodriguez',
    role: 'Founder, Horizon Studios',
    content: 'Professional, creative, and incredibly easy to work with. Our documentary came out better than we ever hoped. Highly recommend!',
    rating: 5,
    date: '2023-12-20',
  },
  {
    id: '4',
    name: 'Michael Park',
    role: 'Brand Manager, Nexus Co',
    content: 'Fast turnaround without compromising quality. The commercial drove incredible engagement for our product launch.',
    rating: 5,
    date: '2023-12-10',
  },
];

const projectTypes = [
  'Brand Film',
  'Documentary',
  'Music Video',
  'Commercial',
  'Event Coverage',
  'Social Media Content',
  'Other',
];

export default function Feedback() {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectType: '',
    rating: 0,
    comment: '',
  });
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.rating === 0) return;

    setIsSubmitting(true);

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          project_type: formData.projectType || 'Not specified',
          rating: formData.rating,
          message: formData.comment,
        },
        EMAILJS_PUBLIC_KEY
      );

      setIsSubmitted(true);
      setFormData({ name: '', email: '', projectType: '', rating: 0, comment: '' });

      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleRatingClick = (rating: number) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  // Sort testimonials by date (newest first)
  const sortedTestimonials = [...approvedTestimonials].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="section-padding pb-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-white/40 text-sm tracking-[0.3em] uppercase">Testimonials</span>
            <h1 className="text-5xl md:text-6xl font-bold mt-2 mb-6">Client Feedback</h1>
            <p className="text-white/60 text-lg max-w-2xl">
              Hear from the amazing people I&apos;ve had the pleasure of working with.
              Your feedback helps me grow and improve.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="section-padding pt-0">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
            {sortedTestimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                className="glass-card-hover rounded-xl p-8 relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                {/* Quote Icon */}
                <div className="absolute top-6 right-6 text-white/5">
                  <Quote size={40} />
                </div>

                {/* Rating */}
                <div className="flex gap-1 mb-5">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={16}
                      className={i < testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-white/20'}
                    />
                  ))}
                </div>

                {/* Content */}
                <p className="text-white/70 mb-6 leading-relaxed">
                  &ldquo;{testimonial.content}&rdquo;
                </p>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <span className="text-white font-medium">
                      {testimonial.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-white font-medium">{testimonial.name}</p>
                    <p className="text-white/40 text-sm">{testimonial.role}</p>
                  </div>
                </div>

                {/* Date */}
                <p className="text-white/30 text-xs mt-4">
                  {new Date(testimonial.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leave Feedback Form */}
      <section className="section-padding bg-[#0a0a0a]">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-white/40 text-sm tracking-[0.3em] uppercase">Share Your Experience</span>
              <h2 className="text-4xl font-bold mt-2 mb-4">Leave Feedback</h2>
              <p className="text-white/50">
                Your feedback is valuable and helps others discover my work.
                All submissions are reviewed before publishing.
              </p>
            </motion.div>

            <motion.div
              className="glass-card rounded-xl p-8 md:p-10"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {isSubmitted ? (
                <motion.div
                  className="text-center py-12"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                    <Check size={40} className="text-green-400" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-2">Thank You!</h3>
                  <p className="text-white/50">
                    Your feedback has been submitted for review. We appreciate your time!
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm text-white/50 mb-2">Your Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:border-white/30 focus:outline-none transition-colors"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-white/50 mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:border-white/30 focus:outline-none transition-colors"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-white/50 mb-2">Project Type</label>
                    <select
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-white/30 focus:outline-none transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-black">Select project type</option>
                      {projectTypes.map(type => (
                        <option key={type} value={type} className="bg-black">{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* Rating */}
                  <div>
                    <label className="block text-sm text-white/50 mb-3">Your Rating *</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <motion.button
                          key={star}
                          type="button"
                          onClick={() => handleRatingClick(star)}
                          onMouseEnter={() => setHoveredRating(star)}
                          onMouseLeave={() => setHoveredRating(0)}
                          className="p-1 transition-colors"
                          whileHover={{ scale: 1.2 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Star
                            size={28}
                            className={`transition-colors ${
                              star <= (hoveredRating || formData.rating)
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-white/20'
                            }`}
                          />
                        </motion.button>
                      ))}
                    </div>
                    {formData.rating > 0 && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-white/40 text-sm mt-2"
                      >
                        {formData.rating === 5 && 'Excellent!'}
                        {formData.rating === 4 && 'Very Good'}
                        {formData.rating === 3 && 'Good'}
                        {formData.rating === 2 && 'Fair'}
                        {formData.rating === 1 && 'Poor'}
                      </motion.p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm text-white/50 mb-2">Your Feedback *</label>
                    <textarea
                      name="comment"
                      value={formData.comment}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:border-white/30 focus:outline-none transition-colors resize-none"
                      placeholder="Share your experience working with us..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || formData.rating === 0}
                    className="w-full py-4 bg-white text-black font-semibold rounded-lg hover:bg-white/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Submit Feedback
                      </>
                    )}
                  </button>

                  <p className="text-white/30 text-sm text-center">
                    Your feedback will be reviewed before publishing.
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
